import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { AuthContextValue, LoginCredentials, User } from '../types/auth.types'
import { Permission } from '../types/auth.types'
import { supabase } from '../lib/supabase'
import {
  loginUser,
  logoutUser,
  loginWithGoogle,
  checkPermission,
  updateUserPermissions as serviceUpdatePermissions,
  profileToUser,
} from '../services/auth.service'

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser]   = useState<User | null>(null)
  const [isLoading, setIsLoading]       = useState(true)
  const [impersonating, setImpersonating] = useState<User | null>(null)

  // ── Restore session on mount + listen for auth changes ───────
  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()

        if (profile) setCurrentUser(profileToUser(profile, session.user.email))
      }
      setIsLoading(false)
    })

    // Listen for sign-in / sign-out / token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setCurrentUser(null)
        setImpersonating(null)
        return
      }

      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()

        if (profile) {
          const user = profileToUser(profile, session.user.email)
          setCurrentUser(user)

          // Store Google tokens in profile if this was an OAuth login
          if (session.provider_token) {
            await supabase.from('profiles').update({
              google_provider_token: session.provider_token,
              google_refresh_token:  session.provider_refresh_token ?? null,
            }).eq('id', session.user.id)
          }
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const result = await loginUser(credentials)
    if (result.success) setCurrentUser(result.user)
    return result
  }, [])

  const loginGoogle = useCallback(async () => {
    await loginWithGoogle()
    // Redirect happens — AuthCallback page handles the session
  }, [])

  const logout = useCallback(async () => {
    await logoutUser(impersonating ? null : currentUser)
    setCurrentUser(null)
    setImpersonating(null)
  }, [currentUser, impersonating])

  const hasPermission = useCallback((permission: Permission): boolean => {
    const activeUser = impersonating ?? currentUser
    return checkPermission(activeUser, permission)
  }, [currentUser, impersonating])

  const isRole = useCallback((role: User['role'] | User['role'][]): boolean => {
    const activeUser = impersonating ?? currentUser
    if (!activeUser) return false
    const roles = Array.isArray(role) ? role : [role]
    return roles.includes(activeUser.role)
  }, [currentUser, impersonating])

  const updateUserPermissions = useCallback((userId: string, permissions: Permission[]) => {
    serviceUpdatePermissions(userId, permissions)
    if (currentUser?.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, permissions } : prev)
    }
  }, [currentUser])

  const startImpersonation = useCallback((user: User) => setImpersonating(user), [])
  const stopImpersonation  = useCallback(() => setImpersonating(null), [])

  const value: AuthContextValue & { loginGoogle: () => Promise<void> } = {
    currentUser:  impersonating ?? currentUser,
    isLoading,
    isAuthenticated: !!currentUser,
    login,
    loginGoogle,
    logout,
    hasPermission,
    isRole,
    updateUserPermissions,
    impersonating,
    startImpersonation,
    stopImpersonation,
  }

  return <AuthContext.Provider value={value as AuthContextValue}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue & { loginGoogle?: () => Promise<void> } {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx as AuthContextValue & { loginGoogle?: () => Promise<void> }
}
